import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

import { RISK_STYLES } from "@/constants/riskLevels";
import { useNotifications, useAppActions } from "@/store/appStore";
import { cn } from "@/utils";

type NotificationType = "success" | "error" | "warning" | "info";

/**
 * Маппинг типа уведомления на наш единый палитру риск-уровней.
 * success → A (зелёный), error → D (красный), warning → C (оранжевый), info → FYI (синий).
 */
const TYPE_TO_RISK: Record<NotificationType, keyof typeof RISK_STYLES> = {
  success: "A",
  error: "D",
  warning: "C",
  info: "FYI",
};

/**
 * Минималистичный toast в Linear/Stripe-стиле:
 * белая карточка с тонкой границей, цветовой dot слева вместо большой иконки,
 * compact spacing, без heavy shadow.
 */
export const NotificationManager: React.FC = () => {
  const notifications = useNotifications();
  const { removeNotification } = useAppActions();

  return (
    <div className="fixed top-[64px] right-5 z-50 flex flex-col gap-2 w-[320px] pointer-events-none">
      <AnimatePresence>
        {notifications.map((notification) => {
          const riskKey = TYPE_TO_RISK[notification.type as NotificationType] ?? "FYI";
          const accent = RISK_STYLES[riskKey].color;

          return (
            <motion.div
              key={notification.id}
              layout
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 16, transition: { duration: 0.15 } }}
              transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
              className={cn(
                "pointer-events-auto group relative",
                "bg-surface-elevated border border-line rounded-card shadow-shell-md",
                "py-2.5 pl-3 pr-2.5",
              )}
            >
              <div className="flex items-start gap-2.5">
                {/* Status dot — единственный цветной акцент */}
                <span
                  aria-hidden
                  className="mt-[5px] w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: accent, boxShadow: `0 0 0 3px ${accent}1A` }}
                />

                <div className="flex-1 min-w-0">
                  <div className="text-fs-xs font-semibold text-ink-1 leading-snug truncate">{notification.title}</div>
                  {notification.message && (
                    <div className="mt-0.5 text-fs-xs text-ink-3 leading-snug">{notification.message}</div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => removeNotification(notification.id)}
                  aria-label="Закрыть"
                  className={cn(
                    "shrink-0 grid place-items-center w-5 h-5 rounded-md -mr-0.5 -mt-0.5",
                    "text-ink-4 hover:text-ink-1 hover:bg-surface-hover transition-colors",
                    "opacity-0 group-hover:opacity-100 focus:opacity-100",
                  )}
                >
                  <X className="w-3 h-3" strokeWidth={2} />
                </button>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
